import React, { createContext, useContext, useState } from "react";
import { Customer } from "../types/customer";
import {
  createCustomer,
  deleteCustomerApi,
  getCustomers,
  updateCustomerApi,
} from "../api/customerApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface CustomerContextProps {
  customers: Customer[];
  filteredCustomers: Customer[];
  addCustomer: (customer: Customer) => void;
  updateCustomer: (customer: Customer) => void;
  deleteCustomer: (id: number) => void;
  lisOfCustomers: () => Promise<void>;
  searchCustomers: (query: string) => void;
}

const CustomerContext = createContext<CustomerContextProps | undefined>(undefined);

export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);

  const addCustomer = async (customer: Customer) => {
    const created = await handleWithAlert(
      () => createCustomer(customer),
      {
        successMessage: "Customer created successfully!",
        errorMessage: "Failed to create customer",
        showSuccess: true,
      }
    );
    if (created) {
      lisOfCustomers();
      setFilteredCustomers(prev => [...prev, created]);
    }
  };

  const updateCustomer = async (updatedCustomer: Customer) => {
    const updated = await handleWithAlert(
      () => updateCustomerApi(updatedCustomer.id.toString(), updatedCustomer),
      {
        successMessage: "Customer updated successfully!",
        errorMessage: "Failed to update customer",
        showSuccess: true,
      }
    );
    if (updated) {
      setCustomers(prev => prev.map(c => (c.id === updatedCustomer.id ? updated : c)));
      setFilteredCustomers(prev => prev.map(c => (c.id === updatedCustomer.id ? updated : c)));
    }
  };

  const deleteCustomer = async (id: number) => {
    const deleted = await handleWithAlert(
      () => deleteCustomerApi(id.toString()),
      {
        successMessage: "Customer deleted successfully!",
        errorMessage: "Failed to delete customer",
        showSuccess: true,
      }
    );
    if (deleted !== null) {
      setCustomers(prev => prev.filter(c => c.id !== id));
      setFilteredCustomers(prev => prev.filter(c => c.id !== id));
    }
  };

  const lisOfCustomers = async () => {
    const list = await handleWithAlert(
      () => getCustomers(),
      {
        errorMessage: "Failed to fetch customer list",
      }
    );
    if (list) {
      setCustomers(list);
      setFilteredCustomers(list);
    }
  };

  const searchCustomers = (query: string) => {
    const lowerQuery = query.toLowerCase();
    const filtered = customers.filter(customer =>
      customer.name.toLowerCase().includes(lowerQuery) ||
      customer.phone1.toLowerCase().includes(lowerQuery) ||
      customer.phone2?.toLowerCase().includes(lowerQuery) ||
      customer.customerCode.toLowerCase().includes(lowerQuery)
    );
    setFilteredCustomers(filtered);
  };

  return (
    <CustomerContext.Provider value={{ customers, filteredCustomers, addCustomer, updateCustomer, deleteCustomer, lisOfCustomers, searchCustomers }}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) throw new Error("useCustomer must be inside CustomerProvider");
  return context;
};
